import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { WhatsAppLink } from "../components/WhatsAppLink";
import {
  StudentAgendaItem,
  useStudentAgenda,
  useStudentProfile,
} from "../api/studentAgenda";
import { useAuth } from "../auth/AuthContext";
import { UI_LOADING } from "../constants/uiLabels";
import "./InstructorMobilePage.css";
import "./StudentMobilePage.css";

type ViewMode = "today" | "tomorrow" | "week" | "month";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
}

function fmtFullDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

function fmtMinutes(min: number): string {
  if (!min) return "0 dk";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} dk`;
  if (m === 0) return `${h} sa`;
  return `${h} sa ${m} dk`;
}

const STATUS_LABELS: Record<string, { label: string; tone: "ok" | "warn" | "info" | "muted" }> = {
  planned: { label: "Planlandi", tone: "info" },
  in_progress: { label: "Devam Ediyor", tone: "warn" },
  completed: { label: "Tamamlandi", tone: "ok" },
  cancelled: { label: "İptal", tone: "muted" },
};

const LESSON_TYPE_LABELS: Record<string, string> = {
  normal: "Normal",
  special: "Ozel",
  extra: "Ek",
};

export default function StudentMobilePage() {
  const { session } = useAuth();
  const [view, setView] = useState<ViewMode>("today");

  const { startDate, endDate } = useMemo(() => {
    if (view === "today") return { startDate: todayIso(), endDate: todayIso() };
    if (view === "tomorrow") return { startDate: shiftIso(1), endDate: shiftIso(1) };
    if (view === "week") return { startDate: todayIso(), endDate: shiftIso(6) };
    return { startDate: todayIso(), endDate: shiftIso(29) };
  }, [view]);

  const profileQuery = useStudentProfile();
  const agendaQuery = useStudentAgenda(startDate, endDate);

  const grouped = useMemo(() => {
    const map = new Map<string, StudentAgendaItem[]>();
    if (!agendaQuery.data) return map;
    for (const item of agendaQuery.data.items) {
      const key = item.starts_at.slice(0, 10);
      const arr = map.get(key) || [];
      arr.push(item);
      map.set(key, arr);
    }
    return map;
  }, [agendaQuery.data]);

  return (
    <div className="m-page sm-page">
      <header className="m-header sm-header">
        <div>
          <span className="m-header__hello">Merhaba</span>
          <strong className="m-header__name">
            {profileQuery.data?.full_name || session?.user?.full_name || "Kursiyer"}
          </strong>
        </div>
        <Link to="/login" className="m-header__pc-link" onClick={() => localStorage.removeItem("kp_auth_v1")}>
          Cikis
        </Link>
      </header>

      {profileQuery.data && (
        <section className="sm-profile">
          <div className="sm-profile__row">
            {profileQuery.data.license_class && (
              <span className="sm-chip sm-chip--accent">{profileQuery.data.license_class}</span>
            )}
            {profileQuery.data.exam_date && (
              <span className="sm-chip">
                Direksiyon: <strong>{fmtFullDate(profileQuery.data.exam_date)}</strong>
                {profileQuery.data.days_to_exam !== null && profileQuery.data.days_to_exam >= 0 && (
                  <span className="sm-chip__sub"> · {profileQuery.data.days_to_exam} Gün Kaldı</span>
                )}
              </span>
            )}
          </div>

          <div className="sm-stats">
            <div className="sm-stat">
              <div className="sm-stat__num">{profileQuery.data.total_completed}</div>
              <div className="sm-stat__label">Yapilan Ders</div>
            </div>
            <div className="sm-stat">
              <div className="sm-stat__num">{fmtMinutes(profileQuery.data.total_completed_minutes)}</div>
              <div className="sm-stat__label">Toplam Sure</div>
            </div>
            <div className="sm-stat">
              <div className="sm-stat__num">{profileQuery.data.total_km.toLocaleString("tr-TR")}</div>
              <div className="sm-stat__label">Toplam KM</div>
            </div>
            <div className="sm-stat sm-stat--accent">
              <div className="sm-stat__num">{profileQuery.data.total_upcoming}</div>
              <div className="sm-stat__label">Yaklasan Ders</div>
            </div>
          </div>

          {profileQuery.data.next_starts_at && (
            <div className="sm-next">
              <div className="sm-next__label">Bir sonraki dersiniz</div>
              <strong>
                {fmtFullDate(profileQuery.data.next_starts_at)} · {fmtTime(profileQuery.data.next_starts_at)}
              </strong>
            </div>
          )}
        </section>
      )}

      <nav className="m-tabs sm-tabs">
        <button type="button" className={view === "today" ? "is-active" : ""} onClick={() => setView("today")}>
          Bugun
        </button>
        <button type="button" className={view === "tomorrow" ? "is-active" : ""} onClick={() => setView("tomorrow")}>
          Yarin
        </button>
        <button type="button" className={view === "week" ? "is-active" : ""} onClick={() => setView("week")}>
          7 Gun
        </button>
        <button type="button" className={view === "month" ? "is-active" : ""} onClick={() => setView("month")}>
          30 Gun
        </button>
      </nav>

      {agendaQuery.loading && <div className="m-info">{UI_LOADING}</div>}
      {agendaQuery.error && <div className="m-error">{agendaQuery.error}</div>}

      {agendaQuery.data && agendaQuery.data.items.length === 0 && !agendaQuery.loading && (
        <div className="m-empty">
          <div className="m-empty__icon">~</div>
          <div className="m-empty__text">Bu Dönemde dersiniz bulunmuyor.</div>
        </div>
      )}

      {agendaQuery.data && agendaQuery.data.items.length > 0 && view !== "week" && view !== "month" && (
        <div className="m-list">
          {agendaQuery.data.items.map((item) => (
            <StudentAgendaCard key={item.appointment_id} item={item} />
          ))}
        </div>
      )}

      {agendaQuery.data && agendaQuery.data.items.length > 0 && (view === "week" || view === "month") && (
        <div className="m-week">
          {Array.from(grouped.entries()).map(([day, items]) => (
            <section key={day} className="m-week__day">
              <h3 className="m-week__day-title">{fmtDay(day)}</h3>
              <div className="m-list">
                {items.map((item) => (
                  <StudentAgendaCard key={item.appointment_id} item={item} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function StudentAgendaCard({ item }: { item: StudentAgendaItem }) {
  const status = STATUS_LABELS[item.status] || { label: item.status, tone: "muted" as const };
  const lessonLabel = LESSON_TYPE_LABELS[item.lesson_type] || item.lesson_type;

  const phoneHref = item.instructor_phone ? `tel:${item.instructor_phone}` : null;
  const wpHref = item.instructor_phone
    ? `https://wa.me/${item.instructor_phone.replace(/[^0-9]/g, "")}`
    : null;

  return (
    <article className="m-card sm-card">
      <header className="m-card__head">
        <div className="m-card__time">
          <strong>{fmtTime(item.starts_at)}</strong>
          <span className="m-card__time-sep">-</span>
          <span>{fmtTime(item.ends_at)}</span>
          <span className="m-card__duration">· {fmtMinutes(item.duration_minutes)}</span>
        </div>
        <span className={`m-badge m-badge--${status.tone}`}>{status.label}</span>
      </header>

      <div className="m-card__row">
        <div className="m-card__icon" aria-hidden>
          E
        </div>
        <div className="m-card__main">
          <strong className="m-card__title">{item.instructor_name}</strong>
          <div className="m-card__sub">Eğitmen</div>
        </div>
        <div className="m-card__contacts">
          {phoneHref && (
            <a href={phoneHref} className="m-iconbtn" aria-label="Ara">
              Ara
            </a>
          )}
          {wpHref && <WhatsAppLink href={wpHref} className="m-iconbtn m-iconbtn--wp" size={18} />}
        </div>
      </div>

      <div className="m-card__row m-card__row--muted">
        <div className="m-card__veh">
          <span className="mono">{item.vehicle_plate}</span>
          {item.vehicle_brand_model && <span> · {item.vehicle_brand_model}</span>}
          {item.vehicle_gear_type && (
            <span> · {item.vehicle_gear_type === "automatic" ? "Otomatik" : "Manuel"}</span>
          )}
        </div>
      </div>

      <div className="m-card__row m-card__row--meta">
        <span className={`m-tag m-tag--${item.lesson_type}`}>{lessonLabel}</span>
        {item.has_report ? (
          <span className="m-tag m-tag--ok">
            Rapor:{" "}
            {item.report_attended === false
              ? "Gelmedi"
              : `${item.report_driving_score !== null ? `${item.report_driving_score}/5 puan` : "Tamam"}`}
            {item.report_km_delta !== null && item.report_attended !== false && (
              <> · +{item.report_km_delta} km</>
            )}
          </span>
        ) : item.status === "completed" ? (
          <span className="m-tag m-tag--warn">Rapor Bekleniyor</span>
        ) : null}
      </div>
    </article>
  );
}
